import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Xmark, Check, OpenInBrowser, ChatBubble } from "iconoir-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export default function TelegramDialog({ open, onClose, memberId }) {
    const { t } = useT();
    const [status, setStatus] = useState(null);
    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !memberId) return;
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/telegram/status/${memberId}`);
                if (alive) setStatus(r.data);
            } catch {
                if (alive) setStatus({ configured: false });
            }
        })();
        return () => { alive = false; };
    }, [open, memberId]);

    const requestLink = async () => {
        setLoading(true);
        try {
            const r = await api.post("/telegram/connect", { member_id: memberId });
            setLinkData(r.data);
        } catch (e) {
            toast.error(t("err.generic"));
        } finally {
            setLoading(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm(t("telegram.confirm_disconnect"))) return;
        setLoading(true);
        try {
            await api.post("/telegram/disconnect", { member_id: memberId });
            const r = await api.get(`/telegram/status/${memberId}`);
            setStatus(r.data);
            setLinkData(null);
            toast.success(t("telegram.disconnected"));
        } catch {
            toast.error(t("err.generic"));
        } finally {
            setLoading(false);
        }
    };

    const copyLink = async () => {
        if (!linkData?.deep_link) return;
        try {
            await navigator.clipboard.writeText(linkData.deep_link);
            toast.success(t("invite.copied"));
        } catch (e) { console.warn("components/TelegramDialog.jsx:", e); }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white brut-border brut-shadow rounded-3xl p-6 max-w-md w-full"
                        data-testid="telegram-dialog"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{t("telegram.eyebrow")}</div>
                                <div className="font-display text-2xl font-bold tracking-tight mt-1">
                                    {t("telegram.title")}
                                </div>
                            </div>
                            <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#faf5ea] flex items-center justify-center" data-testid="close-telegram-btn">
                                <Xmark className="w-4 h-4" />
                            </button>
                        </div>

                        {status === null && (
                            <div className="text-sm text-[#5b6d63]">{t("common.loading")}</div>
                        )}

                        {status && !status.configured && (
                            <div className="brut-card p-4 bg-[#fbeecb]">
                                <div className="text-sm font-semibold mb-1">{t("telegram.not_configured_title")}</div>
                                <div className="text-xs text-[#5b6d63]">{t("telegram.not_configured_body")}</div>
                            </div>
                        )}

                        {status?.configured && status.connected && (
                            <div className="space-y-3">
                                <div className="brut-card p-4 bg-[#e8f1e4] flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-[#2F6B43] text-white flex items-center justify-center shrink-0">
                                        <Check className="w-4 h-4" strokeWidth={3} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold">{t("telegram.connected")}</div>
                                        {status.telegram_username && (
                                            <div className="text-xs text-[#5b6d63] truncate">@{status.telegram_username}</div>
                                        )}
                                    </div>
                                </div>
                                <p className="text-sm text-[#5b6d63] leading-relaxed">
                                    {t("telegram.connected_body")}
                                </p>
                                <button
                                    onClick={disconnect}
                                    disabled={loading}
                                    className="w-full text-sm text-[#a14444] font-semibold py-2 hover:bg-[#fbeaea] rounded-lg transition-colors"
                                    data-testid="telegram-disconnect-btn"
                                >
                                    {t("telegram.disconnect")}
                                </button>
                            </div>
                        )}

                        {status?.configured && !status.connected && !linkData && (
                            <div className="space-y-4">
                                <p className="text-sm text-[#5b6d63] leading-relaxed">
                                    {t("telegram.intro")}
                                </p>
                                <p className="text-xs text-[#5b6d63]">
                                    {t("telegram.no_telegram")}{" "}
                                    <a
                                        href="https://telegram.org/apps"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold text-[#2F6B43] underline"
                                    >
                                        {t("telegram.download")}
                                    </a>
                                </p>
                                <button
                                    onClick={requestLink}
                                    disabled={loading}
                                    className="brut-btn w-full justify-center"
                                    data-testid="telegram-get-link-btn"
                                >
                                    <ChatBubble className="w-4 h-4" /> {loading ? t("common.loading") : t("telegram.get_link")}
                                </button>
                            </div>
                        )}

                        {linkData?.deep_link && (
                            <div className="space-y-3 mt-2">
                                <div className="brut-card p-3 bg-[#faf5ea] text-xs font-mono break-all" data-testid="telegram-deep-link">
                                    {linkData.deep_link}
                                </div>
                                <div className="flex gap-2">
                                    <a
                                        href={linkData.deep_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="brut-btn flex-1 justify-center"
                                        data-testid="telegram-open-btn"
                                    >
                                        <OpenInBrowser className="w-4 h-4" /> {t("telegram.open_telegram")}
                                    </a>
                                    <button
                                        onClick={copyLink}
                                        className="brut-btn brut-btn-secondary"
                                        data-testid="telegram-copy-btn"
                                    >
                                        {t("invite.copy")}
                                    </button>
                                </div>
                                <p className="text-[11px] text-[#5b6d63]">
                                    {t("telegram.expires", { min: linkData.expires_in_min })}
                                </p>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
